import type { NotificationCategory, NotificationPriority } from '~/shared/types/notification.js'

/**
 * Interface for notification creation options
 */
export interface NotificationCreateOptions {
  title: string
  body: string
  category?: NotificationCategory
  priority?: NotificationPriority
  linkType?: 'task' | 'subkrewe' | 'member' | 'url' | 'none'
  linkTarget?: string
  metadata?: Record<string, unknown>
  sendEmail?: boolean
  emailTemplate?: string
  actionText?: string
}

/**
 * Composable providing easy creation of various notification types
 */
export const useNotificationCreator = () => {
  const notificationsStore = useNotificationsStore()

  /**
   * Create a notification for a specific member
   */
  const createNotification = async (
    memberId: string,
    options: NotificationCreateOptions,
  ): Promise<string | null> => {
    try {
      return await notificationsStore.createNotification(memberId, {
        title: options.title,
        body: options.body,
        category: options.category || 'system',
        priority: options.priority || 'normal',
        linkType: options.linkType || 'none',
        linkTarget: options.linkTarget,
        metadata: options.metadata || {},
        // Future email integration
        emailScheduled: options.sendEmail || false,
      })
    }
    catch (error) {
      console.error('Failed to create notification:', error)
      return null
    }
  }

  /**
   * Create a system notification
   */
  const createSystemNotification = async (
    memberId: string,
    title: string,
    body: string,
    options: Partial<NotificationCreateOptions> = {},
  ): Promise<string | null> => {
    return createNotification(memberId, {
      title,
      body,
      category: 'system',
      priority: options.priority || 'normal',
      linkType: options.linkType,
      linkTarget: options.linkTarget,
      metadata: options.metadata || {},
      sendEmail: options.sendEmail || false,
      emailTemplate: options.emailTemplate,
    })
  }

  /**
   * Create a task-related notification
   */
  const createTaskNotification = async (
    memberId: string,
    title: string,
    body: string,
    taskId: string,
    options: Partial<NotificationCreateOptions> = {},
  ): Promise<string | null> => {
    return createNotification(memberId, {
      title,
      body,
      category: 'task',
      priority: options.priority || 'normal',
      linkType: 'task',
      linkTarget: taskId,
      metadata: {
        taskId,
        ...options.metadata,
      },
      sendEmail: options.sendEmail || false,
      emailTemplate: options.emailTemplate,
    })
  }

  /**
   * Create a membership-related notification
   */
  const createMembershipNotification = async (
    memberId: string,
    title: string,
    body: string,
    subkreweId: string,
    options: Partial<NotificationCreateOptions> = {},
  ): Promise<string | null> => {
    return createNotification(memberId, {
      title,
      body,
      category: 'membership',
      priority: options.priority || 'normal',
      linkType: 'subkrewe',
      linkTarget: subkreweId,
      metadata: {
        subkreweId,
        actionText: options.actionText,
        ...options.metadata,
      },
      sendEmail: options.sendEmail || false,
      emailTemplate: options.emailTemplate,
    })
  }

  /**
   * Create an admin notification
   */
  const createAdminNotification = async (
    memberId: string,
    title: string,
    body: string,
    options: Partial<NotificationCreateOptions> = {},
  ): Promise<string | null> => {
    return createNotification(memberId, {
      title,
      body,
      category: 'admin',
      priority: options.priority || 'high',
      linkType: options.linkType || 'none',
      linkTarget: options.linkTarget,
      metadata: options.metadata || {},
      sendEmail: options.sendEmail !== undefined ? options.sendEmail : true, // Default to sending email for admin notifications
      emailTemplate: options.emailTemplate,
    })
  }

  /**
   * Create a notification for SubKrewe application approval
   */
  const createMembershipApprovedNotification = async (
    memberId: string,
    subkreweId: string,
    subkreweName: string,
    captainName?: string,
    memberType?: string,
  ): Promise<string | null> => {
    const isRedshirt = String(memberType || '').toLowerCase() === 'redshirt'
    const title = `${isRedshirt ? '[Redshirt] ' : ''}Your SubKrewe Application was Approved`
    const body = `Your ${isRedshirt ? 'redshirt ' : ''}application to join ${subkreweName} has been approved${captainName ? ` by ${captainName}` : ''}.`

    return createMembershipNotification(
      memberId,
      title,
      body,
      subkreweId,
      {
        priority: 'high',
        sendEmail: true,
        emailTemplate: 'join-request-approved',
        metadata: {
          subkreweId,
          subkreweName,
          captainName,
          memberType: isRedshirt ? 'redshirt' : (memberType ?? undefined),
          isRedshirt,
        },
      },
    )
  }

  /**
   * Create a notification for SubKrewe application denial
   */
  const createMembershipDeniedNotification = async (
    memberId: string,
    subkreweId: string,
    subkreweName: string,
    reason?: string,
    captainName?: string,
    memberType?: string,
  ): Promise<string | null> => {
    const isRedshirt = String(memberType || '').toLowerCase() === 'redshirt'
    const title = `${isRedshirt ? '[Redshirt] ' : ''}Your SubKrewe Application was Denied`
    const body = `Your ${isRedshirt ? 'redshirt ' : ''}application to join ${subkreweName} was not approved${captainName ? ` by ${captainName}` : ''}.${reason ? ` Reason: ${reason}` : ''}`

    return createMembershipNotification(
      memberId,
      title,
      body,
      subkreweId,
      {
        priority: 'normal',
        sendEmail: true,
        emailTemplate: 'join-request-denied',
        metadata: {
          subkreweId,
          subkreweName,
          captainName,
          reason,
          memberType: isRedshirt ? 'redshirt' : (memberType ?? undefined),
          isRedshirt,
        },
      },
    )
  }

  /**
   * Create a notification for a new join request (sent to captain)
   */
  const createNewJoinRequestNotification = async (
    captainId: string,
    subkreweId: string,
    subkreweName: string,
    applicantName: string,
    applicantId: string,
    memberType?: string,
    applicantEmail?: string,
    applicantPhone?: string,
  ): Promise<string | null> => {
    const isRedshirt = String(memberType || '').toLowerCase() === 'redshirt'
    const title = `${isRedshirt ? '[Redshirt] ' : ''}New SubKrewe Join Request`

    // Build contact info section
    const contactParts: string[] = []
    if (applicantEmail) contactParts.push(`Email: ${applicantEmail}`)
    if (applicantPhone) contactParts.push(`Phone: ${applicantPhone}`)
    const contactInfo = contactParts.length > 0 ? `\n\nContact info:\n${contactParts.join('\n')}` : ''

    const body = `${applicantName}${isRedshirt ? ' (Redshirt)' : ''} has requested to join your SubKrewe ${subkreweName}.${contactInfo}`

    return createMembershipNotification(
      captainId,
      title,
      body,
      subkreweId,
      {
        priority: 'normal',
        sendEmail: true,
        emailTemplate: 'new-join-request',
        metadata: {
          subkreweId,
          subkreweName,
          applicantName,
          applicantId,
          applicantEmail,
          applicantPhone,
          memberType: isRedshirt ? 'redshirt' : (memberType ?? undefined),
          isRedshirt,
        },
      },
    )
  }

  /**
 * Create a notification for a canceled join request (sent to captain)
 */
  const createJoinRequestCanceledNotification = async (
    captainId: string,
    subkreweId: string,
    subkreweName: string,
    applicantId: string,
    applicantName: string,
    reason?: string,
    memberType?: string,
  ): Promise<string | null> => {
    const isRedshirt = String(memberType || '').toLowerCase() === 'redshirt'
    const title = `${isRedshirt ? '[Redshirt] ' : ''}SubKrewe Join Request Canceled`
    const body = `${applicantName}${isRedshirt ? ' (Redshirt)' : ''} has canceled their request to join ${subkreweName}.${reason ? ` Reason: ${reason}` : ''}`

    return createMembershipNotification(
      captainId,
      title,
      body,
      subkreweId,
      {
        priority: 'normal',
        sendEmail: true,
        emailTemplate: 'join-request-canceled',
        metadata: {
          subkreweId,
          subkreweName,
          applicantId,
          applicantName,
          reason,
          memberType: isRedshirt ? 'redshirt' : (memberType ?? undefined),
          isRedshirt,
        },
      },
    )
  }

  /**
   * Create a notification for successful parade check-in
   */
  const createCheckinConfirmedNotification = async (
    memberId: string,
    memberName: string,
    year: number,
    checkedInByName: string,
    subkreweName?: string,
    options?: {
      vipCount?: number
      parkingCount?: number
      childrenCount?: number
    },
  ): Promise<string | null> => {
    const title = `You're Checked In for ${year}!`
    const body = `You've been checked in for the ${year} parade${checkedInByName ? ` by ${checkedInByName}` : ''}.${subkreweName ? ` SubKrewe: ${subkreweName}` : ''}`

    return createSystemNotification(
      memberId,
      title,
      body,
      {
        priority: 'normal',
        sendEmail: true,
        emailTemplate: 'checkin-confirmed',
        linkType: 'url',
        // Home, not /checkin: that page is for volunteers checking others in
        linkTarget: '/',
        metadata: {
          year,
          memberName,
          checkedInByName,
          subkreweName,
          vipCount: options?.vipCount ?? 0,
          parkingCount: options?.parkingCount ?? 0,
          childrenCount: options?.childrenCount ?? 0,
        },
      },
    )
  }

  /**
   * Create a notification for lineup published (sent to captains)
   */
  const createLineupPublishedNotification = async (
    captainId: string,
    captainName: string,
    year: number,
    subkreweName: string,
    paradeOrder: number,
    zoneName: string,
    zoneDescription: string,
    zoneStartTime?: string,
    zoneEndTime?: string,
    lineupDescription?: string,
  ): Promise<string | null> => {
    const title = `${year} Parade Lineup Published!`
    const body = `The ${year} parade lineup is out! ${subkreweName} is #${paradeOrder} in ${zoneName}.`

    return createSystemNotification(
      captainId,
      title,
      body,
      {
        priority: 'high',
        sendEmail: true,
        emailTemplate: 'lineup-published',
        linkType: 'url',
        linkTarget: '/lineup',
        metadata: {
          year,
          captainName,
          subkreweName,
          paradeOrder,
          zoneName,
          zoneDescription,
          zoneStartTime,
          zoneEndTime,
          lineupDescription,
        },
      },
    )
  }

  /**
   * Create a notification for lineup position change (sent to captains)
   */
  const createLineupChangedNotification = async (
    captainId: string,
    captainName: string,
    year: number,
    subkreweName: string,
    paradeOrder: number,
    previousParadeOrder: number,
    zoneName: string,
    zoneDescription: string,
    zoneStartTime?: string,
    zoneEndTime?: string,
  ): Promise<string | null> => {
    const title = `Lineup Position Changed`
    const body = `${subkreweName} moved from #${previousParadeOrder} to #${paradeOrder} in the ${year} parade lineup.`

    return createSystemNotification(
      captainId,
      title,
      body,
      {
        priority: 'normal',
        sendEmail: true,
        emailTemplate: 'lineup-changed',
        linkType: 'url',
        linkTarget: '/lineup',
        metadata: {
          year,
          captainName,
          subkreweName,
          paradeOrder,
          previousParadeOrder,
          zoneName,
          zoneDescription,
          zoneStartTime,
          zoneEndTime,
        },
      },
    )
  }

  return {
    createNotification,
    createSystemNotification,
    createTaskNotification,
    createMembershipNotification,
    createAdminNotification,
    createMembershipApprovedNotification,
    createMembershipDeniedNotification,
    createNewJoinRequestNotification,
    createJoinRequestCanceledNotification,
    createCheckinConfirmedNotification,
    createLineupPublishedNotification,
    createLineupChangedNotification,
  }
}

export default useNotificationCreator
